<template>
  <div
    id="iframe-root"
    :class="{ 'is-iframe': isInIframe }"
  >
    <HeroBanner />
    <AppHeader />
    
    <div class="app-content">
      <router-view @open-betslip-modal="openBetslipModal" />
      <AppFooter />
      <ThemeToggle class="desktop-only" />
      <ToastComponent
        v-if="toast"
        :title="toast.title"
        :message="toast.message"
        :type="toast.type"
        :duration="toast.duration"
      />
      <BetslipModal
        :is-open="modalStore.showBetslip"
        @close="closeBetslipModal"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, watch, ref, defineExpose } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import HeroBanner from './components/base/HeroBanner.vue';
import AppHeader from '@/components/base/AppHeader.vue'
import AppFooter from './components/base/AppFooter.vue';
import ToastComponent from '@/components/ToastComponent.vue'
import { useToast } from '@/composables/useToast'
import ThemeToggle from '@/components/ThemeToggle.vue'
import BetslipModal from '@/components/BetslipModal.vue'
import { useModalStore } from '@/stores/modal'
import { useAuthStore } from '@/stores/auth'
import { useGameStore } from '@/stores/game'
import { useUpcomingDrawsStore } from '@/stores/upcomingDraws'
import { getThemeParamFromUrl, initializeTheme, setTheme } from '@/composables/useTheme'
import { resolveThemeName } from '@/utils/themeLoader'
import { iframeCommunication } from '@/utils/iframeCommunication'
import { setApiBaseUrl } from '@/api'
import { useI18n } from 'vue-i18n'

const { toast, showToast } = useToast()
const modalStore = useModalStore()
const authStore = useAuthStore()
const gameStore = useGameStore()
const upcomingDrawsStore = useUpcomingDrawsStore()
const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const iframeCleanupHandlers: Array<() => void> = []

const isInitialized = ref(false)

const openBetslipModal = () => {
  modalStore.openBetslip()
}

const closeBetslipModal = () => {
  modalStore.closeBetslip()
}

const refreshAccountScopedCatalog = async () => {
  upcomingDrawsStore.fetchDraws(1)
  gameStore.clearProducts()
}

// Watch for account changes in URL
watch(
  () => route.query.account,
  async (newAccount, oldAccount) => {
    if (newAccount !== oldAccount && newAccount && typeof newAccount === 'string') {
      authStore.account = newAccount
      sessionStorage.setItem('auth_account', newAccount)
      
      try {
        await initializeTheme(true)
        const currency = sessionStorage.getItem('auth_currency')
        if (currency) {
          await gameStore.fetchGameSettings(false)
        }
        await refreshAccountScopedCatalog()
      } catch (error) {
        console.error('Failed to re-initialize theme:', error)
      }
    }
  },
  { immediate: false }
)

// Only apply game-settings theme when URL has no theme param (URL wins)
watch(
  () => gameStore.getBetSettings?.theme,
  async (newTheme) => {
    if (newTheme && !getThemeParamFromUrl()) {
      try {
        await setTheme(resolveThemeName(newTheme))
      } catch (error) {
        console.error('Failed to update theme from game settings:', error)
      }
    }
  },
  { immediate: false }
)

const isInIframe = iframeCommunication.isRunningInIframe()

onMounted(async () => {
  const handleParentLogout = () => {
    authStore.logout({ silent: true, reason: 'host-request' })
    modalStore.closeBetslip()
    showToast({
      title: t('common.loggedOut'),
      message: t('common.sessionClosed'),
      type: 'info',
      duration: 4000
    })
    router.replace({ path: '/next-draws', query: {} })
  }

  const handleParentNavigate = (data?: Record<string, unknown>) => {
    try {
      if (!data) return
      const target = data.route || data.path || data.name
      if (!target || typeof target !== 'string') return
      
      const query = (data.query && typeof data.query === 'object') 
        ? data.query as Record<string, string> 
        : undefined

      if (target.startsWith('/')) {
        router.push({ path: target, query })
      } else {
        router.push({ name: target, query })
      }
    } catch (e) {
      console.error('Iframe navigation failed:', e)
    }
  }

  // CORE HANDSHAKE HANDLER: This is where configuration is synchronized
  const handleParentReady = async (data?: Record<string, unknown>) => {
    console.log('[App] Handshake received from parent:', data)
    if (data?.config && typeof data.config === 'object') {
      const config = data.config as Record<string, any>
      
      // 1. Synchronize Authentication/Account state
      const accountStr =
        typeof config.account === 'string'
          ? config.account.trim()
          : config.account != null
            ? String(config.account).trim()
            : ''
      if (accountStr) {
        authStore.account = accountStr
        sessionStorage.setItem('auth_account', accountStr)
      }
      const tokenStr =
        typeof config.token === 'string'
          ? config.token.trim()
          : config.token != null
            ? String(config.token).trim()
            : ''
      if (tokenStr) {
        authStore.token = tokenStr
        sessionStorage.setItem('auth_token', tokenStr)
      }
      if (config.currency) {
        sessionStorage.setItem('auth_currency', config.currency)
      }

      // 2. Apply dynamic API base URL
      if (config.GAME_SERVER_URL) {
        setApiBaseUrl(config.GAME_SERVER_URL)
      }

      // 3. Trigger initial settings fetch with correct URL/Headers
      try {
        await initializeTheme(true)
        const currency = config.currency || sessionStorage.getItem('auth_currency')
        if (currency) {
          await gameStore.fetchGameSettings(false)
        }
        await refreshAccountScopedCatalog()
      } catch (e) {
        console.warn('Initialization after handshake failed:', e)
      }
    } else {
      // No config provided by parent, just initialize theme
      await initializeTheme()
    }
    
    // 4. Reveal the app content
    isInitialized.value = true
  }

  // Register communication listeners
  iframeCleanupHandlers.push(iframeCommunication.on('logout', handleParentLogout))
  iframeCleanupHandlers.push(iframeCommunication.on('navigate', handleParentNavigate))
  iframeCleanupHandlers.push(iframeCommunication.on('parent-ready', handleParentReady))

  if (isInIframe) {
    document.body.classList.add('iframe-mode')
    document.documentElement.classList.add('iframe-mode')
    
    // Measure and report height
    iframeCommunication.startObserving('iframe-root')
    
    // START HANDSHAKE: Tell parent child is ready to receive configuration
    iframeCommunication.sendReady()

    // Safety timeout: If handshake doesn't complete in 4s, proceed with default init
    setTimeout(async () => {
      if (!isInitialized.value) {
        console.warn('[App] Handshake timeout - proceeding with default initialization')
        await initializeTheme()
        isInitialized.value = true
      }
    }, 4000)

    // Handle deep links from URL params
    const gameSlug = (route.query.game || route.query.gameSlug) as string | undefined
    if (gameSlug && typeof gameSlug === 'string' && gameSlug.trim()) {
      const slug = gameSlug.trim()
      const restQuery = { ...route.query }
      delete restQuery.game
      delete restQuery.gameSlug
      router.replace({ path: `/lottery/${slug}`, query: restQuery as Record<string, string> })
    }
  } else {
    // Standard standalone mode
    const rawAccount =
      authStore.account ||
      sessionStorage.getItem('auth_account') ||
      (route.query.account as string | undefined)
    const account =
      typeof rawAccount === 'string'
        ? rawAccount.trim()
        : rawAccount != null
          ? String(rawAccount).trim()
          : ''
    if (account) {
      authStore.account = account
      sessionStorage.setItem('auth_account', account)
    }

    const rawToken = route.query.token as string | undefined
    const token =
      typeof rawToken === 'string' ? rawToken.trim() : rawToken != null ? String(rawToken).trim() : ''
    if (token) {
      authStore.token = token
      sessionStorage.setItem('auth_token', token)
    }

    try {
      await initializeTheme()
      const currency = sessionStorage.getItem('auth_currency')
      if (account && currency) {
        await gameStore.fetchGameSettings(false)
      }
    } catch (e) {
      console.warn('Failed to load initial settings:', e)
    }
    
    isInitialized.value = true
  }
})

onUnmounted(() => {
  iframeCommunication.destroy()
  iframeCleanupHandlers.forEach((cleanup) => cleanup())
})

defineExpose({
  openBetslipModal,
  closeBetslipModal
})
</script>

<style>
#app {
  max-width: 1280px;
  margin: 0 auto;
  background: var(--bg-primary) !important;
  box-sizing: border-box;
  min-height: 100vh;
  font-family: 'Ubuntu', sans-serif;
  position: relative;
  color: var(--text-primary);
  overflow-x: hidden;
}

html.iframe-mode,
body.iframe-mode {
  overflow-y: auto !important;
  /* background: transparent !important; */
  height: auto !important;
  min-height: 0 !important;
}

body.iframe-mode #app {
  min-height: 0 !important;
  height: auto !important;
  /* background: transparent !important; */
  display: block !important;
  overflow: visible !important; /* Allows children to expand for measurement */
  overflow-x: hidden !important;
}

body.iframe-mode .app-content,
body.iframe-mode .main-content-wrapper,
body.iframe-mode .betslip-wrapper,
body.iframe-mode .detail-view,
body.iframe-mode .upcoming-draws-view,
body.iframe-mode .betslip,
body.iframe-mode .betslip-content,
body.iframe-mode .selected-draws-container,
body.iframe-mode .draw-dropdown-menu,
body.iframe-mode .modal-content,
body.iframe-mode .modal-body,
body.iframe-mode .modal-box,
body.iframe-mode .modal-overlay,
body.iframe-mode .betslip-modal,
body.iframe-mode .mobile-view-bets-container,
body.iframe-mode .view-bets-container,
body.iframe-mode .mobile-back-container {
  height: auto !important;
  min-height: 0 !important;
  max-height: none !important;
  overflow: visible !important;
}

/* Restore fixed/absolute positioning for overlays */
body.iframe-mode .modal-overlay,
body.iframe-mode .desktop-modal-overlay {
  position: fixed !important;
  background-color: var(--shadow-color) !important;
  z-index: 9999 !important;
  align-items: flex-start !important;
  padding-top: 40px !important;
}

/* Restore modal scrolling in iframe mode (overrides the bulk overflow:visible rule above) */
body.iframe-mode .modal-overlay {
  overflow-y: auto !important;
  height: 100% !important;
}

body.iframe-mode .modal-box {
  max-height: calc(100vh - 80px) !important;
  display: flex !important;
  flex-direction: column !important;
}

body.iframe-mode .modal-body {
  overflow-y: auto !important;
  flex: 1 !important;
  min-height: 0 !important;
}

/* Lock background scroll when a modal is open in iframe mode */
html.iframe-mode.modal-open,
body.iframe-mode.modal-open {
  overflow: hidden !important;
}

body.iframe-mode .mobile-view-bets-container,
body.iframe-mode .view-bets-container {
  position: fixed !important;
  bottom: 0 !important;
  left: 0 !important;
  right: 0 !important;
  background: var(--bg-dark) !important;
  z-index: 100 !important;
  margin-top: 0 !important;
  padding: 16px !important;
}

body.iframe-mode .view-bets-container {
  border-top: 1px solid var(--tab-button-inactive) !important;
}

/* Strip all internal scrollbars globally in iframe mode */
body.iframe-mode * {
  scrollbar-width: none !important;
  -ms-overflow-style: none !important;
}

body.iframe-mode *::-webkit-scrollbar {
  display: none !important;
  width: 0 !important;
  height: 0 !important;
}

body.iframe-mode {
  margin: 0;
  padding: 0;
  overflow-y: auto !important;
}


.app-content {
  padding: 8px;
}

.is-iframe .app-content {
  padding: 0;
}

@media (max-width: 768px) {
  #app {
    max-width: 100%;
    margin: 0;
    padding: 0;
  }
  .app-content {
    padding: 0px;
    margin: 0px;
  }
}

::-webkit-scrollbar {
  display: none;
}

.desktop-only {
  display: block;
}

.mobile-only {
  display: none;
}

@media (max-width: 768px) {
  .desktop-only {
    display: none;
  }
  
  .mobile-only {
    display: block;
  }
}

::-webkit-scrollbar-thumb {
  display: none;
}

::-webkit-scrollbar-track {
  display: none;
}

::-webkit-scrollbar-corner {
  display: none;
}

::-webkit-scrollbar-button {
  display: none;
}

::-webkit-scrollbar-track-piece {
  display: none;
}

html, body {
  margin: 0;
  padding: 0;
  min-height: 100vh;
  overflow-x: hidden;
}

body {
  background: var(--bg-primary);
}
</style>
